import NewTaskForm from '@app/components/apps/common/NewTaskForm';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { Button, Form, InputNumber } from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface NewProductRegistrationProps {
  onSubmit: (data: any) => void;
  editable: boolean;
  form: any;
  productsOptions: any[];
  countryOptions: any[];
  productTypeOptions: any[];
  formFields: any[];
  status: string | null;
  related_id: number | null;
  appeal?: string | null;
  appeal_no?: number | null;
  isRenewable: boolean;
  editable_cdata: boolean;
  PR_type?: string | null;
  pr_id?: string | null;
  pr_feed_related_forms?: Record<
    string,
    {
      form: string;
      id: number;
      year: string;
      status: string;
    } | null
  >;
  product_type?: string;
}

const ImpFreeSalesNewReg = forwardRef<any, NewProductRegistrationProps>(
  (
    {
      onSubmit,
      editable,
      status,
      form,
      productsOptions,
      countryOptions,
      productTypeOptions,
      formFields,
      related_id,
      PR_type,
      pr_id,
      appeal,
      editable_cdata,
      appeal_no,
      pr_feed_related_forms,
      product_type,
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const categoryId = Number(localStorage.getItem('selected_category_id'));

    const handleFormSubmit = () => {
      form
        .validateFields()
        .then((values: any) => {
          //console.log('ImpFreeSalesNewReg submitted data:', values);
          onSubmit(values);
        })
        .catch((errorInfo: any) => {
          console.error('Validation failed:', errorInfo);
        });
    };

    useImperativeHandle(ref, () => ({
      submitForm: handleFormSubmit,
      setData: (data: any) => {
        console.log('Received data in setData:', data);
        console.log('Current formFields:', formFields);
        const formValues = { ...data };
        formFields.forEach((field) => {
          if (data[field.field_id] !== undefined) {
            let fieldValue = data[field.field_id];

            if (field.input_type === 'DATE' && typeof fieldValue === 'string') {
              fieldValue = fieldValue.replace(/\\\//g, '/');

              const parts = fieldValue.split('/');
              if (parts.length === 3 && parts[0].length === 4) {
                fieldValue = `${parts[2]}/${parts[1]}/${parts[0]}`;
              }
            }

            formValues[field.field_id] = fieldValue;
          }
        });
        form.setFieldsValue(formValues);
      },
      getData: () => form.getFieldsValue(true),
      form,
    }));

    const handleRelatedIdClick = () => {
      if (related_id) {
        navigate('/view-product-registrations', { state: { id: related_id } });
      }
    };

    useEffect(() => {
      console.log('pr feed related forms in ImpFreeSalesNewReg:', pr_feed_related_forms);
    }, [pr_feed_related_forms]);

    const isFormEditable = editable && (status === null || status === 'DRAFT');

    const feedFormsConfig = {
      FORM_THREE: {
        name: 'Raw Material Usage - Form III',
        view: '/view-feed-usage',
        create: '/add-feed-usage',
      },
      FORM_FOUR: {
        name: 'Animal Feed Production - Form IV',
        view: '/view-feed-production',
        create: '/add-feed-productions',
      },
      FORM_FIVE: {
        name: 'Animal Feed Importation - Form V',
        view: '/view-feed-import',
        create: '/add-feed-import',
      },
      FORM_SIX: {
        name: 'Animal Feed Production Forecast - Form VI',
        view: '/view-feed-forecast',
        create: '/add-feed-forecast',
      },
      FORM_SEVEN: {
        name: 'Expected Quantities of Raw Materials Imported / Locally Purchased - Form VII',
        view: '/view-feed-expected',
        create: '/add-exp-ingredient',
      },
    };
    return (
      <Form
        form={form}
        name="newProductRegistrationForm"
        onFinish={handleFormSubmit}
        initialValues={form.getFieldsValue()}
        size="small"
        labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
        labelAlign="left"
        labelWrap
        wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
      >
        <BaseRow gutter={[16, 16]}>
          {related_id && (
            <BaseCol xs={24} md={8} lg={12}>
              <Form.Item label="Related Task ID">
                <a
                  onClick={handleRelatedIdClick}
                  style={{ cursor: 'pointer', color: '#8C1C03', textDecoration: 'underline' }}
                >
                  #{related_id}
                </a>
              </Form.Item>
            </BaseCol>
          )}
          {appeal && appeal_no !== 0 && (
            <BaseCol xs={24} md={8} lg={12}>
              <Form.Item label="Appeal Attempt">{appeal}</Form.Item>
            </BaseCol>
          )}
          <BaseCol xs={24} md={8} lg={12}>
            <Form.Item
              name="pr_product_id"
              label="Product Category"
              rules={[{ required: true, message: 'Product Category is required' }]}
              getValueProps={(value) => ({
                value: productsOptions.find((opt) => opt.value === value)?.label || '',
              })}
            >
              {!isFormEditable ? (
                <BaseInput readOnly />
              ) : (
                <BaseSelect
                  placeholder="Select Product Category"
                  options={productsOptions}
                  disabled={!isFormEditable}
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
              )}
            </Form.Item>
          </BaseCol>

          <BaseCol xs={24} md={8} lg={12}>
            <Form.Item
              name="pr_type"
              label="Registration Type"
              rules={[{ required: true, message: 'Registration Type is required' }]}
            >
              {!isFormEditable ? (
                <BaseInput readOnly value={form.getFieldValue('pr_type') || ''} />
              ) : (
                <BaseSelect
                  placeholder="Select Registration Type"
                  options={productTypeOptions}
                  disabled={!isFormEditable}
                />
              )}
            </Form.Item>
          </BaseCol>
          {product_type !== 'LOCATION' && (
            <BaseCol xs={24} md={8} lg={12}>
              <Form.Item
                name="country_id"
                label="Importer Country"
                rules={[{ required: true, message: 'Importer Country is required' }]}
                getValueProps={(value) => ({
                  value: countryOptions.find((opt) => opt.value === value)?.label || '',
                })}
              >
                {!isFormEditable ? (
                  <BaseInput readOnly />
                ) : (
                  <BaseSelect
                    placeholder="Select Impoter Country"
                    options={countryOptions}
                    disabled={!isFormEditable}
                    showSearch
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  />
                )}
              </Form.Item>
            </BaseCol>
          )}
          <hr />
          <NewTaskForm
            formFields={formFields}
            form={form}
            isFormEditable={isFormEditable}
            cdata={editable_cdata}
            pr_id={pr_id}
          />
        </BaseRow>

        {/* Feed Forms & Annexures */}
        {PR_type === 'RENEW' && product_type === 'MULTIPLE' && pr_feed_related_forms && (
          <>
            <br />
            <hr />
            <br />
            <BaseRow>
              <BaseCol xs={24}>
                <h2>Feed Forms & Annexures</h2>
                <BaseRow gutter={[16, 16]}>
                  {Object.entries(feedFormsConfig).map(([key, cfg], index) => {
                    const formData = pr_feed_related_forms[key];
                    return (
                      <BaseCol key={index} xs={24} md={12}>
                        <div
                          style={{
                            padding: '16px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            textAlign: 'left',
                          }}
                        >
                          <span style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>{cfg.name}</span>
                          {formData && formData.id ? (
                            <Button
                              type="primary"
                              onClick={() => navigate(cfg.view, { state: { id: formData.id } })}
                              style={{ marginRight: '8px' }}
                            >
                              View Form
                            </Button>
                          ) : (
                            <Button type="default" onClick={() => navigate(cfg.create)}>
                              Create Form
                            </Button>
                          )}
                        </div>
                      </BaseCol>
                    );
                  })}
                </BaseRow>
              </BaseCol>
            </BaseRow>
          </>
        )}
      </Form>
    );
  },
);

export default ImpFreeSalesNewReg;
